import { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  GenericDialog,
  GenericCombobox,
  TableRow,
  TableCell,
  Table
} from '@/common'
import { OrganizationUser } from '@/api/OrganizationUserModel'
import {
  useDeleteUserRole,
  useOrganizationUserRoles,
  useOrganizationUsers,
  useUpdateUserRole
} from '@/shared/hooks/api/organizationUsers'
import { useSession } from '@/shared/hooks/useSession'
import toast from 'react-hot-toast'

export default function OrganizationUserPermissionsDialog({
  user,
  isOpen,
  close,
  onApprove,
  onAfterLeave
}: {
  user: OrganizationUser
  isOpen: boolean
  onAfterLeave: () => void
  close: () => void
  onApprove: (selectedEmissionReportId: string, importedGoodId: string) => void
}) {
  const {
    session: {
      id: selfUserId,
      organization: { id: organizationId }
    }
  } = useSession()
  const { t } = useTranslation()
  const { data: roles } = useOrganizationUserRoles(organizationId)

  const { mutateAsync: updateUserRole } = useUpdateUserRole(organizationId, user.id)
  const { mutateAsync: deleteUserRole } = useDeleteUserRole(organizationId, user.id)
  const { data: organizationUsers } = useOrganizationUsers(organizationId)
  const [role, setRole] = useState<string | null>(null)

  const onSaveClick = useCallback(async () => {
    if (role) {
      if (user.id === selfUserId) {
        toast.error(t('organizationUsers:permissionsDialog.errors.ownRole'))
        return
      }

      await updateUserRole({ roleSlug: role })
      setRole(null)
    }
  }, [role, onApprove])

  const onDeleteRole = useCallback(async (roleSlug: string | null) => {
    if (roleSlug) {
      const admins = organizationUsers?.filter(user =>
        user.permissions.some(permission => permission.roleSlug === 'admin')
      )
      if (user.id === selfUserId) {
        toast.error(t('organizationUsers:permissionsDialog.errors.ownRole'))
        return
      }

      const isLastAdmin = admins?.length ? admins?.length === 1 : false
      if (isLastAdmin && roleSlug === 'admin') {
        toast.error(t('organizationUsers:permissionsDialog.errors.lastAdmin'))
        return
      }

      await deleteUserRole({ roleSlug })
    }
  }, [])

  const roleOptions = useMemo(
    () =>
      roles?.map(role => ({
        label: t(`organizationUsers:roles.${role}`),
        value: role
      })),
    [roles, t]
  )

  return (
    <GenericDialog
      isOpen={isOpen}
      close={close}
      onAfterLeave={onAfterLeave}
      title={t('organizationUsers:permissionsDialog.title')}
      size="lg"
    >
      <div className="space-y-4">
        <Table>
          {user.permissions.map(permission => (
            <TableRow key={permission.roleSlug}>
              <TableCell>{t(`organizationUsers:roles.${permission.roleSlug}`)}</TableCell>
              <TableCell className="w-1/4 text-center">
                <Button variant="danger" onClick={() => onDeleteRole(permission.roleSlug)}>
                  {t('organizationUsers:permissionsDialog.actions.revoke')}
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </Table>
        <p className="text-sm my-2">{t('organizationUsers:permissionsDialog.description')}</p>
        <GenericCombobox
          placeholder={t('organizationUsers:permissionsDialog.fields.selectRole')}
          items={roleOptions || []}
          value={role ? { value: role, label: t(`organizationUsers:roles.${role}`) } : null}
          onChange={evt => setRole(evt?.value || null)}
        />
        <div className="space-y-6 mt-4">
          <div className="gap-2 flex justify-end">
            <Button variant="neutral" onClick={close}>
              {t('organizationUsers:permissionsDialog.actions.cancel')}
            </Button>
            <Button disabled={!role} onClick={onSaveClick}>
              {t('organizationUsers:permissionsDialog.actions.save')}
            </Button>
          </div>
        </div>
      </div>
    </GenericDialog>
  )
}
